import React, { useState, useRef, useCallback } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Pencil, Trash2, Loader2, Plus, Sparkles } from 'lucide-react';
import type { InvoiceDetectionResult } from '@/services/invoiceService';
import { ocrInvoiceRegion, detectInvoiceType, lookupCompany } from '@/services/invoiceService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface MetadataField {
  value: string;
  bbox: BBox | null;
}

interface InvoiceTypeApprovalProps {
  detection: InvoiceDetectionResult;
  fileBase64: string;
  fileMimeType: string;
  onConfirm: (type: 'purchase' | 'sales', editedMetadata: Record<string, MetadataField>, items: PurchaseItem[]) => void;
  onBack: () => void;
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Field labels & colors
// ---------------------------------------------------------------------------

const FIELD_LABELS: Record<string, { en: string; zh: string }> = {
  invoice_number: { en: 'Invoice ID', zh: '發票號碼' },
  invoice_date: { en: 'Invoice Date', zh: '發票日期' },
  seller_name: { en: 'Seller Name', zh: '賣方名稱' },
  seller_unified_number: { en: 'Seller ID', zh: '賣方統編' },
  buyer_name: { en: 'Buyer Name', zh: '買方名稱' },
  buyer_unified_number: { en: 'Buyer ID', zh: '買方統編' },
  sale_amount: { en: 'Sale Amount (Prior Tax)', zh: '銷售額(未稅)' },
  tax_amount: { en: 'Tax Amount', zh: '稅額' },
  total_amount: { en: 'Total Amount', zh: '總計' },
  order_id: { en: 'Order ID', zh: '訂單編號' },
};

const FIELD_KEYS = Object.keys(FIELD_LABELS);

const BBOX_COLORS: Record<string, string> = {
  invoice_number: '#3b82f6',
  invoice_date: '#8b5cf6',
  seller_name: '#059669',
  seller_unified_number: '#10b981',
  buyer_name: '#d97706',
  buyer_unified_number: '#f59e0b',
  sale_amount: '#0ea5e9',
  tax_amount: '#ec4899',
  total_amount: '#ef4444',
  order_id: '#64748b',
};

// Purchase items (bbox-able fields)
interface PurchaseItem {
  name: MetadataField;
  spec: MetadataField;
  qty: MetadataField;
  price: MetadataField;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const InvoiceTypeApproval: React.FC<InvoiceTypeApprovalProps> = ({
  detection,
  fileBase64,
  fileMimeType,
  onConfirm,
  onBack,
  onCancel,
}) => {
  const getDefaultType = (): 'purchase' | 'sales' => {
    if (detection.business_binding?.direction === 'purchase') return 'purchase';
    if (detection.business_binding?.direction === 'sales') return 'sales';
    if (detection.invoice_type !== 'unknown') return detection.invoice_type as 'purchase' | 'sales';
    return 'purchase';
  };
  const [selectedType, setSelectedType] = useState<'purchase' | 'sales'>(getDefaultType());

  const initMetadata = (): Record<string, MetadataField> => {
    const fields: Record<string, MetadataField> = {};
    for (const key of FIELD_KEYS) {
      const raw = detection.metadata?.[key];
      fields[key] = {
        value: raw?.value != null ? String(raw.value) : '',
        bbox: raw?.bbox ?? null,
      };
    }
    return fields;
  };
  const [metadata, setMetadata] = useState<Record<string, MetadataField>>(initMetadata);
  const [activeField, setActiveField] = useState<string | null>(null);
  const [drawingField, setDrawingField] = useState<string | null>(null);
  const [purchaseItems, setPurchaseItems] = useState<PurchaseItem[]>([]);
  const [aiLoading, setAiLoading] = useState(false);

  const pageDims = detection.metadata?.page_dimensions as unknown as { width: number; height: number } | undefined;
  const pageW = pageDims?.width || 1;
  const pageH = pageDims?.height || 1;

  const confidenceLevel = detection.confidence >= 0.8 ? 'high' : detection.confidence >= 0.5 ? 'medium' : 'low';
  const ConfidenceIcon = confidenceLevel === 'high' ? CheckCircle : confidenceLevel === 'medium' ? AlertTriangle : XCircle;
  const confidenceColor = confidenceLevel === 'high' ? 'text-green-600' : confidenceLevel === 'medium' ? 'text-amber-500' : 'text-red-500';

  const updateFieldValue = (key: string, value: string) => {
    setMetadata(prev => ({ ...prev, [key]: { ...prev[key], value } }));
  };

  const updateBBox = (key: string, coord: keyof BBox, value: number) => {
    setMetadata(prev => {
      const field = prev[key];
      const bbox = field.bbox || { x: 0, y: 0, w: 100, h: 30 };
      return { ...prev, [key]: { ...field, bbox: { ...bbox, [coord]: value } } };
    });
  };

  const deleteBBox = (key: string) => {
    setMetadata(prev => ({ ...prev, [key]: { ...prev[key], bbox: null } }));
  };

  // "Use AI" — call /detect (Claude Vision) to auto-fill all fields
  const handleUseAI = async () => {
    setAiLoading(true);
    try {
      const result = await detectInvoiceType({
        file_base64: fileBase64,
        mime_type: fileMimeType,
      });
      // Map AI detection result metadata into our fields
      if (result.metadata) {
        setMetadata(prev => {
          const updated = { ...prev };
          for (const key of FIELD_KEYS) {
            const aiField = result.metadata?.[key];
            if (aiField?.value != null) {
              updated[key] = {
                value: String(aiField.value),
                bbox: aiField.bbox ?? prev[key]?.bbox ?? null,
              };
            }
          }
          return updated;
        });
      }
      // Update direction if AI detected it
      if (result.business_binding?.direction === 'purchase' || result.business_binding?.direction === 'sales') {
        setSelectedType(result.business_binding.direction as 'purchase' | 'sales');
      }
    } catch (err) {
      console.error('[InvoiceTypeApproval] AI detection failed:', err);
    } finally {
      setAiLoading(false);
    }
  };

  // Purchase items
  const emptyField = (): MetadataField => ({ value: '', bbox: null });

  const addPurchaseItem = () => {
    setPurchaseItems(prev => [...prev, { name: emptyField(), spec: emptyField(), qty: emptyField(), price: emptyField() }]);
  };

  const updatePurchaseItemValue = (index: number, field: keyof PurchaseItem, value: string) => {
    setPurchaseItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: { ...updated[index][field], value } };
      return updated;
    });
  };

  const updatePurchaseItemBBox = (index: number, field: keyof PurchaseItem, bbox: BBox | null) => {
    setPurchaseItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: { ...updated[index][field], bbox } };
      return updated;
    });
  };

  const removePurchaseItem = (index: number) => {
    setPurchaseItems(prev => prev.filter((_, i) => i !== index));
  };

  // Start drawing for a purchase item field
  const startDrawingItemField = (index: number, field: keyof PurchaseItem) => {
    const drawKey = `item_${index}_${field}`;
    setActiveField(drawKey);
    setDrawingField(drawKey);
  };

  // Handle bbox drawn for item fields
  const handleItemBBoxDrawn = async (drawKey: string, bbox: BBox) => {
    const match = drawKey.match(/^item_(\d+)_(\w+)$/);
    if (!match) return;
    const index = parseInt(match[1]);
    const field = match[2] as keyof PurchaseItem;

    updatePurchaseItemBBox(index, field, bbox);
    setDrawingField(null);

    // OCR the cropped region
    const croppedBase64 = cropImageRegion(bbox);
    if (!croppedBase64) return;

    setOcrLoading(drawKey);
    try {
      const ocrResult = await ocrInvoiceRegion({
        image_base64: croppedBase64,
        region_bbox: bbox,
        page_dimensions: { width: pageW, height: pageH },
      });
      if (ocrResult.text) {
        const cleanText = ocrResult.text.replace(/\n/g, ' ').trim();
        updatePurchaseItemValue(index, field, cleanText);
      }
    } catch (err) {
      console.error('[InvoiceTypeApproval] OCR failed for', drawKey, err);
    } finally {
      setOcrLoading(null);
    }
  };

  const [ocrLoading, setOcrLoading] = useState<string | null>(null); // field key being OCR'd

  const startDrawing = (key: string) => {
    setActiveField(key);
    setDrawingField(key);
  };

  // Crop a region from the page image using canvas and return base64 PNG
  const cropImageRegion = useCallback((bbox: BBox): string | null => {
    try {
      const img = new window.Image();
      img.src = `data:${fileMimeType};base64,${fileBase64}`;

      const canvas = document.createElement('canvas');
      // bbox is in page coordinates (same as rendered image pixel coordinates)
      canvas.width = bbox.w;
      canvas.height = bbox.h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(img, bbox.x, bbox.y, bbox.w, bbox.h, 0, 0, bbox.w, bbox.h);
      // Return base64 without the data URI prefix
      return canvas.toDataURL('image/png').split(',')[1];
    } catch {
      return null;
    }
  }, [fileBase64, fileMimeType]);

  const handleBBoxDrawn = async (key: string, bbox: BBox) => {
    // Route item field draws to the item handler
    if (key.startsWith('item_')) {
      await handleItemBBoxDrawn(key, bbox);
      return;
    }

    // Update bbox immediately
    setMetadata(prev => ({ ...prev, [key]: { ...prev[key], bbox } }));
    setDrawingField(null);

    // Crop the region and send to OCR
    const croppedBase64 = cropImageRegion(bbox);
    if (!croppedBase64) return;

    setOcrLoading(key);
    try {
      const ocrResult = await ocrInvoiceRegion({
        image_base64: croppedBase64,
        region_bbox: bbox,
        page_dimensions: { width: pageW, height: pageH },
      });

      if (ocrResult.text) {
        const cleanText = ocrResult.text.replace(/\n/g, ' ').trim();
        setMetadata(prev => ({
          ...prev,
          [key]: { ...prev[key], value: cleanText, bbox },
        }));

        // Auto-lookup company name when a 統一編號 is OCR'd
        if ((key === 'seller_unified_number' || key === 'buyer_unified_number') && /^\d{8}$/.test(cleanText)) {
          try {
            const result = await lookupCompany(cleanText);
            if (result.found && result.company) {
              const nameKey = key === 'seller_unified_number' ? 'seller_name' : 'buyer_name';
              setMetadata(prev => ({
                ...prev,
                [nameKey]: { ...prev[nameKey], value: result.company!.company_name },
              }));
            }
          } catch {
            // Non-critical — company lookup failure doesn't block the flow
          }
        }
      }
    } catch (err) {
      console.error('[InvoiceTypeApproval] OCR failed for', key, err);
    } finally {
      setOcrLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-6xl mx-4 w-full h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Invoice Detection</h2>
            {detection.document_type && (
              <span className="text-xs px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full">
                {detection.document_type_label || detection.document_type}
              </span>
            )}
            {detection.electronic && (
              <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full">
                Electronic
              </span>
            )}
          </div>
          <div className={`flex items-center gap-1.5 ${confidenceColor}`}>
            <ConfidenceIcon className="w-4 h-4" />
            <span className="text-sm font-medium">{Math.round(detection.confidence * 100)}%</span>
          </div>
        </div>

        {/* Split view */}
        <div className="flex-1 flex overflow-hidden">
          {/* LEFT: Metadata + Controls */}
          <div className="w-[380px] shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto p-4 space-y-4">
            {!detection.is_invoice && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-300">May not be an invoice</p>
                </div>
              </div>
            )}

            {detection.copy_type && (
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full shrink-0 ${
                  detection.copy_color === 'red' ? 'bg-red-500' :
                  detection.copy_color === 'green' ? 'bg-green-500' :
                  detection.copy_color === 'blue' ? 'bg-blue-500' : 'bg-gray-400'
                }`} />
                <span className="text-xs text-gray-700 dark:text-gray-300">
                  {detection.copy_type} — {detection.copy_type_label}
                </span>
              </div>
            )}

            {detection.business_binding && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-2">
                <p className="text-xs text-blue-700 dark:text-blue-400">{detection.business_binding.description}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {detection.business_binding.process.map(p => (
                    <span key={p} className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded">
                      {p.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Drawing mode indicator */}
            {drawingField && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-2 text-xs text-amber-800 dark:text-amber-300">
                Draw a box on the image for <strong>{FIELD_LABELS[drawingField]?.en || drawingField.replace(/^item_\d+_/, 'Item ')}</strong>.
                <button onClick={() => setDrawingField(null)} className="ml-2 underline">Cancel</button>
              </div>
            )}

            {/* Use AI button */}
            <button
              onClick={handleUseAI}
              disabled={aiLoading}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/30 disabled:opacity-50 transition-colors"
            >
              {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {aiLoading ? 'AI Detecting...' : 'Use AI (Auto-detect All Fields)'}
            </button>

            {/* Editable metadata fields */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Extracted Fields</p>
              {FIELD_KEYS.map(key => {
                const field = metadata[key];
                const label = FIELD_LABELS[key];
                const color = BBOX_COLORS[key];
                const isActive = activeField === key;

                return (
                  <div
                    key={key}
                    className={`rounded-lg border p-2 transition-colors cursor-pointer ${
                      isActive
                        ? 'border-blue-400 bg-blue-50/50 dark:bg-blue-900/10'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveField(isActive ? null : key)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 flex-1">{label.en} ({label.zh})</span>
                      {/* Edit / Delete bbox buttons */}
                      <button
                        title="Draw bbox"
                        onClick={e => { e.stopPropagation(); startDrawing(key); }}
                        className="p-0.5 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      {field.bbox && (
                        <button
                          title="Remove bbox"
                          onClick={e => { e.stopPropagation(); deleteBBox(key); }}
                          className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        value={field.value}
                        onChange={e => updateFieldValue(key, e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 pr-7"
                        placeholder={`Enter ${label.en.toLowerCase()}`}
                        onClick={e => e.stopPropagation()}
                      />
                      {ocrLoading === key && (
                        <Loader2 className="absolute right-2 top-1.5 w-3.5 h-3.5 text-blue-500 animate-spin" />
                      )}
                    </div>
                    {isActive && field.bbox && (
                      <div className="grid grid-cols-4 gap-1 mt-1.5">
                        {(['x', 'y', 'w', 'h'] as const).map(coord => (
                          <div key={coord}>
                            <label className="text-[10px] text-gray-400 uppercase">{coord}</label>
                            <input
                              type="number"
                              value={field.bbox![coord]}
                              onChange={e => updateBBox(key, coord, parseInt(e.target.value) || 0)}
                              className="w-full px-1 py-0.5 text-xs border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600"
                              onClick={e => e.stopPropagation()}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Purchase Items */}
            <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Purchase Items</p>
                <button
                  onClick={addPurchaseItem}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              {purchaseItems.map((item, i) => {
                const itemFields: { key: keyof PurchaseItem; label: string; placeholder: string }[] = [
                  { key: 'name', label: 'Product', placeholder: 'Product name' },
                  { key: 'spec', label: 'Spec', placeholder: 'Specification' },
                  { key: 'qty', label: 'Qty', placeholder: 'Quantity' },
                  { key: 'price', label: 'Price', placeholder: 'Unit price' },
                ];
                return (
                  <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-600 p-2 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-gray-400">Item {i + 1}</span>
                      <button onClick={() => removePurchaseItem(i)} className="p-0.5 text-gray-400 hover:text-red-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    {itemFields.map(({ key, label, placeholder }) => {
                      const drawKey = `item_${i}_${key}`;
                      const isLoading = ocrLoading === drawKey;
                      return (
                        <div key={key} className="flex items-center gap-1">
                          <div className="relative flex-1">
                            <input
                              value={item[key].value}
                              onChange={e => updatePurchaseItemValue(i, key, e.target.value)}
                              className="w-full px-2 py-1 text-xs border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600 pr-6"
                              placeholder={placeholder}
                            />
                            {isLoading && <Loader2 className="absolute right-1.5 top-1 w-3 h-3 text-blue-500 animate-spin" />}
                          </div>
                          <button
                            title={`Draw bbox for ${label}`}
                            onClick={() => startDrawingItemField(i, key)}
                            className="p-0.5 text-gray-400 hover:text-blue-600 shrink-0"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          {item[key].bbox && (
                            <button
                              title="Remove bbox"
                              onClick={() => updatePurchaseItemBBox(i, key, null)}
                              className="p-0.5 text-gray-400 hover:text-red-500 shrink-0"
                            >
                              <Trash2 className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {purchaseItems.length === 0 && (
                <p className="text-[10px] text-gray-400 italic">No items added. Click + to add purchase items.</p>
              )}
            </div>

            {/* Direction */}
            <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Direction</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedType('purchase')}
                  className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-colors ${
                    selectedType === 'purchase'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Purchase (進貨)
                </button>
                <button
                  onClick={() => setSelectedType('sales')}
                  className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-colors ${
                    selectedType === 'sales'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-medium'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  Sales (銷貨)
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT: Document preview */}
          <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900 p-2">
            <DocumentPreviewWithBBoxes
              fileBase64={fileBase64}
              fileMimeType={fileMimeType}
              metadata={metadata}
              purchaseItems={purchaseItems}
              pageWidth={pageW}
              pageHeight={pageH}
              activeField={activeField}
              drawingField={drawingField}
              onFieldSelect={setActiveField}
              onBBoxChange={(key, bbox) => {
                setMetadata(prev => ({ ...prev, [key]: { ...prev[key], bbox } }));
              }}
              onBBoxDrawn={handleBBoxDrawn}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-between shrink-0">
          <button onClick={onBack} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            ← Back
          </button>
          <div className="flex gap-2">
            <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              Cancel
            </button>
            <button
              onClick={() => onConfirm(selectedType, metadata, purchaseItems)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Document Preview with bboxes, mouse coordinates, and draw mode
// ---------------------------------------------------------------------------

interface DocumentPreviewProps {
  fileBase64: string;
  fileMimeType: string;
  metadata: Record<string, MetadataField>;
  purchaseItems: PurchaseItem[];
  pageWidth: number;
  pageHeight: number;
  activeField: string | null;
  drawingField: string | null;
  onFieldSelect: (key: string | null) => void;
  onBBoxChange: (key: string, bbox: BBox) => void;
  onBBoxDrawn: (key: string, bbox: BBox) => void;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

const DocumentPreviewWithBBoxes: React.FC<DocumentPreviewProps> = ({
  fileBase64,
  fileMimeType,
  metadata,
  purchaseItems,
  pageWidth,
  pageHeight,
  activeField,
  drawingField,
  onFieldSelect,
  onBBoxChange,
  onBBoxDrawn,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [mouseCoord, setMouseCoord] = useState<{ x: number; y: number } | null>(null);
  const [zoom, setZoom] = useState(1);

  // Drawing state
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [drawCurrent, setDrawCurrent] = useState<{ x: number; y: number } | null>(null);

  const imgSrc = `data:${fileMimeType};base64,${fileBase64}`;

  const zoomIn = () => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP));
  const zoomOut = () => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP));
  const zoomReset = () => setZoom(1);

  // Scroll wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setZoom(z => {
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z + delta));
      });
    }
  }, []);

  // Convert mouse event to page coordinates (accounts for zoom via container rect)
  const eventToPageCoord = useCallback((e: React.MouseEvent): { x: number; y: number } | null => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const relX = e.clientX - rect.left;
    const relY = e.clientY - rect.top;
    return {
      x: Math.round((relX / rect.width) * pageWidth),
      y: Math.round((relY / rect.height) * pageHeight),
    };
  }, [pageWidth, pageHeight]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const coord = eventToPageCoord(e);
    setMouseCoord(coord);

    if (drawStart && drawingField) {
      setDrawCurrent(coord);
    }
  }, [eventToPageCoord, drawStart, drawingField]);

  const handleMouseLeave = () => {
    setMouseCoord(null);
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!drawingField) return;
    e.preventDefault();
    const coord = eventToPageCoord(e);
    if (coord) {
      setDrawStart(coord);
      setDrawCurrent(coord);
    }
  }, [drawingField, eventToPageCoord]);

  const handleMouseUp = useCallback(() => {
    if (!drawingField || !drawStart || !drawCurrent) return;
    const x = Math.min(drawStart.x, drawCurrent.x);
    const y = Math.min(drawStart.y, drawCurrent.y);
    const w = Math.abs(drawCurrent.x - drawStart.x);
    const h = Math.abs(drawCurrent.y - drawStart.y);

    if (w > 5 && h > 5) {
      onBBoxDrawn(drawingField, { x, y, w, h });
    }
    setDrawStart(null);
    setDrawCurrent(null);
  }, [drawingField, drawStart, drawCurrent, onBBoxDrawn]);

  // Draw preview bbox
  const drawPreviewBBox = drawStart && drawCurrent && drawingField ? {
    leftPct: (Math.min(drawStart.x, drawCurrent.x) / pageWidth) * 100,
    topPct: (Math.min(drawStart.y, drawCurrent.y) / pageHeight) * 100,
    widthPct: (Math.abs(drawCurrent.x - drawStart.x) / pageWidth) * 100,
    heightPct: (Math.abs(drawCurrent.y - drawStart.y) / pageHeight) * 100,
    color: BBOX_COLORS[drawingField] || '#3b82f6',
  } : null;

  return (
    <div className="relative">
      {/* Zoom controls — fixed at top-left of preview area */}
      <div className="sticky top-0 z-30 flex items-center gap-1 mb-2">
        <button onClick={zoomOut} className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
          −
        </button>
        <button onClick={zoomReset} className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 font-mono min-w-[3rem] text-center">
          {Math.round(zoom * 100)}%
        </button>
        <button onClick={zoomIn} className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
          +
        </button>
        <span className="text-[10px] text-gray-400 ml-1">Ctrl+Scroll to zoom</span>
      </div>

      {/* Zoomable container */}
      <div
        ref={containerRef}
        className="relative inline-block select-none origin-top-left"
        style={{
          lineHeight: 0,
          cursor: drawingField ? 'crosshair' : 'default',
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
      >
        <img
          src={imgSrc}
          alt="Invoice document"
          draggable={false}
          onDragStart={e => e.preventDefault()}
          onLoad={() => setReady(true)}
          className="block max-w-full h-auto rounded shadow-lg pointer-events-none"
        />

        {/* Mouse coordinate + zoom display */}
        {mouseCoord && (
          <div className="absolute top-2 right-2 bg-black/70 text-white text-[10px] px-2 py-1 rounded font-mono z-30 pointer-events-none">
            x: {mouseCoord.x}, y: {mouseCoord.y}
          </div>
        )}

      {/* Existing bbox overlays */}
      {ready && FIELD_KEYS.map(key => {
        const field = metadata[key];
        if (!field.bbox) return null;

        const leftPct = (field.bbox.x / pageWidth) * 100;
        const topPct = (field.bbox.y / pageHeight) * 100;
        const widthPct = (field.bbox.w / pageWidth) * 100;
        const heightPct = (field.bbox.h / pageHeight) * 100;
        const color = BBOX_COLORS[key];
        const isActive = activeField === key;

        return (
          <DraggableBBox
            key={key}
            leftPct={leftPct}
            topPct={topPct}
            widthPct={widthPct}
            heightPct={heightPct}
            color={color}
            label={FIELD_LABELS[key].en}
            isActive={isActive}
            disabled={!!drawingField}
            containerRef={containerRef}
            pageWidth={pageWidth}
            pageHeight={pageHeight}
            onSelect={() => onFieldSelect(key)}
            onBBoxChange={(bbox) => onBBoxChange(key, bbox)}
          />
        );
      })}

      {/* Purchase item bbox overlays */}
      {ready && purchaseItems.map((item, i) => {
        const itemColor = '#6366f1'; // indigo for all item fields
        return (['name', 'spec', 'qty', 'price'] as const).map(field => {
          const f = item[field];
          if (!f.bbox) return null;
          const drawKey = `item_${i}_${field}`;
          return (
            <DraggableBBox
              key={drawKey}
              leftPct={(f.bbox.x / pageWidth) * 100}
              topPct={(f.bbox.y / pageHeight) * 100}
              widthPct={(f.bbox.w / pageWidth) * 100}
              heightPct={(f.bbox.h / pageHeight) * 100}
              color={itemColor}
              label={`#${i + 1} ${field}`}
              isActive={activeField === drawKey}
              disabled={!!drawingField}
              containerRef={containerRef}
              pageWidth={pageWidth}
              pageHeight={pageHeight}
              onSelect={() => onFieldSelect(drawKey)}
              onBBoxChange={(bbox) => onBBoxChange(drawKey, bbox)}
            />
          );
        });
      })}

      {/* Draw preview rect */}
      {drawPreviewBBox && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: `${drawPreviewBBox.leftPct}%`,
            top: `${drawPreviewBBox.topPct}%`,
            width: `${drawPreviewBBox.widthPct}%`,
            height: `${drawPreviewBBox.heightPct}%`,
            border: `2px dashed ${drawPreviewBBox.color}`,
            backgroundColor: `${drawPreviewBBox.color}22`,
            zIndex: 25,
          }}
        />
      )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Draggable + resizable bounding box (percentage-based)
// ---------------------------------------------------------------------------

interface DraggableBBoxProps {
  leftPct: number;
  topPct: number;
  widthPct: number;
  heightPct: number;
  color: string;
  label: string;
  isActive: boolean;
  disabled: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  pageWidth: number;
  pageHeight: number;
  onSelect: () => void;
  onBBoxChange: (bbox: BBox) => void;
}

const DraggableBBox: React.FC<DraggableBBoxProps> = ({
  leftPct, topPct, widthPct, heightPct,
  color, label, isActive, disabled,
  containerRef, pageWidth, pageHeight,
  onSelect, onBBoxChange,
}) => {
  const dragState = useRef<{
    mode: 'move' | 'resize';
    startX: number;
    startY: number;
    origBBox: BBox;
  } | null>(null);

  const screenToPage = useCallback((dxScreen: number, dyScreen: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { dx: 0, dy: 0 };
    return {
      dx: (dxScreen / rect.width) * pageWidth,
      dy: (dyScreen / rect.height) * pageHeight,
    };
  }, [containerRef, pageWidth, pageHeight]);

  const currentBBox: BBox = {
    x: Math.round((leftPct / 100) * pageWidth),
    y: Math.round((topPct / 100) * pageHeight),
    w: Math.round((widthPct / 100) * pageWidth),
    h: Math.round((heightPct / 100) * pageHeight),
  };

  const handlePointerDown = useCallback((e: React.PointerEvent, mode: 'move' | 'resize') => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    dragState.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origBBox: { ...currentBBox },
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [currentBBox, onSelect, disabled]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragState.current) return;
    e.preventDefault();
    const { dx, dy } = screenToPage(
      e.clientX - dragState.current.startX,
      e.clientY - dragState.current.startY,
    );
    const orig = dragState.current.origBBox;

    if (dragState.current.mode === 'move') {
      onBBoxChange({
        x: Math.max(0, Math.round(orig.x + dx)),
        y: Math.max(0, Math.round(orig.y + dy)),
        w: orig.w,
        h: orig.h,
      });
    } else {
      onBBoxChange({
        x: orig.x,
        y: orig.y,
        w: Math.max(10, Math.round(orig.w + dx)),
        h: Math.max(10, Math.round(orig.h + dy)),
      });
    }
  }, [screenToPage, onBBoxChange]);

  const handlePointerUp = useCallback(() => {
    dragState.current = null;
  }, []);

  return (
    <div
      className="absolute group"
      style={{
        left: `${leftPct}%`,
        top: `${topPct}%`,
        width: `${widthPct}%`,
        height: `${heightPct}%`,
        border: `2px solid ${color}`,
        backgroundColor: isActive ? `${color}22` : `${color}11`,
        cursor: disabled ? 'default' : 'move',
        zIndex: isActive ? 20 : 10,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      onPointerDown={e => handlePointerDown(e, 'move')}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClick={e => { e.stopPropagation(); if (!disabled) onSelect(); }}
    >
      <span
        className="absolute -top-4 left-0 text-[10px] font-medium px-1 rounded-t whitespace-nowrap pointer-events-none"
        style={{ backgroundColor: color, color: 'white' }}
      >
        {label}
      </span>

      {!disabled && (
        <div
          className="absolute -bottom-1 -right-1 w-3 h-3 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ backgroundColor: color, cursor: 'nwse-resize' }}
          onPointerDown={e => handlePointerDown(e, 'resize')}
        />
      )}
    </div>
  );
};

export default InvoiceTypeApproval;
